import { Scene, _ } from "~/route/globals";
import { node, icon } from "~/util/dom";
import Items, { IItemData } from "~/route/items";
import { ROUTE_CONST } from "~/route/const";
import { getPosition } from "~/route/utils";
import { getIcon } from "~/util/providers/poiagg";

/**
 * Obal pro:
 * - vyplneny item - ma source, id
 */
export default class ItemPoint {
	_parent: Items;

	_data: IItemData;

	_active: boolean;

	_dom: {
		container: HTMLDivElement;
		inner?: HTMLDivElement;
		position?: HTMLParagraphElement;
	};

	_readOnly: boolean;

	constructor(parent: Items, data: IItemData, readOnly = false) {
		this._parent = parent;
		this._data = data;
		this._active = false;
		this._readOnly = readOnly;

		this._dom = {
			container: node("div", { className: "route-item-point" }),
		};

		this._build();
	}

	destroy() {
		this._dom.inner.removeEventListener("click", this);
		this._dom.inner.removeEventListener("pointerenter", this);
		this._dom.inner.removeEventListener("pointerleave", this);
		this._dom.container.remove();
	}

	get container() {
		return this._dom.container;
	}

	get state() {
		return this._data;
	}

	get geometry() {
		return this._data.geometry;
	}

	/**
	 * Nastaveni nove pozice - pri editaci.
	 *
	 * @param  position Enum pozice
	 * @param  ind  Poradi
	 */
	setPosition(position: number, ind: number) {
		this._dom.position.textContent = getPosition(position, ind);
	}

	/**
	 * Nastaveni stavu.
	 */
	setActive(state: boolean) {
		if (state !== this._active) {
			this._active = state;
			this._dom.container.classList[state ? "add" : "remove"]("hover");
		}
	}

	handleEvent(event: MouseEvent) {
		switch (event.type) {
			case "click":
				Mapy.getComponent("detail").setState({
					id: this._data.id,
					source: this._data.source,
					show: false,
				}).then(() => Mapy.getComponent("history").save(), () => {});
				break;

			case "pointerenter":
				if (this._data) {
					Mapy.CML.state = {
						route: {
							hoverInd: this._data.ind,
						},
					};
				}
				break;

			case "pointerleave":
				Mapy.CML.state = {
					route: {
						hoverInd: -1,
					},
				};
				break;

			default:
				break;
		}
	}

	_build() {
		// vnitrni cast
		this._dom.inner = node("div", { className: "inner" });
		const dadHandler = node("div", { className: "dad dad-handle dad-handle-touch" });
		const iconMove = icon("move");

		!this._readOnly && dadHandler.appendChild(iconMove);

		const cont = node("div", { className: "cont" });
		// oblasti navic do marginu, aby se dalo tahat pri dad
		const dadAreaLeft = node("div", { className: "dad-area dad-handle left" });
		const dadAreaRight = node("div", { className: "dad-area dad-handle right" });

		this._dom.position = node("p", { textContent: getPosition(this._data.position, this._data.ind) });
		const title = node("h4", { className: "dad-handle" });
		const desc = node("p", { className: "subtitle dad-handle", textContent: this._data.desc || this._data.title });
		const clear = node("button", { className: "remove" });
		const bottom = node("div", { className: "bottom" });

		if (this._data.usertitle || this._data.title) {
			title.textContent = this._data.usertitle || this._data.title;
		} else {
			title.textContent = Mapy.getComponent("gps").coordsToFormat(Scene.stringToCoords(this._data.geometry)[0]);
		}

		clear.appendChild(icon("delete"));
		cont.appendChild(this._dom.position);
		cont.appendChild(title);
		cont.appendChild(desc);
		!this._readOnly && cont.appendChild(clear);

		this._dom.inner.appendChild(dadHandler);
		this._dom.inner.appendChild(this._getImage());
		this._dom.inner.appendChild(cont);
		this._dom.inner.appendChild(dadAreaLeft);
		this._dom.inner.appendChild(dadAreaRight);
		this._dom.container.appendChild(this._dom.inner);
		this._dom.container.appendChild(bottom);

		if (this._data.showTrip) {
			const tripBtn = node("button", { className: "circuit noprint", textContent: _("route.item.trip") });

			tripBtn.addEventListener("click", event => {
				event.stopPropagation();
				event.preventDefault();
				this._showTripClick();
			});
			bottom.appendChild(tripBtn);
		}

		// eventy
		// timeout kvuli pointerdown na ikone domu a prace, pokud jsou v moje mapy vyplnene
		setTimeout(() => {
			if (!this._dom.inner) {
				return;
			}
			this._dom.inner.addEventListener("click", this);
		}, 300);
		this._dom.inner.addEventListener("pointerenter", this);
		this._dom.inner.addEventListener("pointerleave", this);

		// timeout kvuli pointerdown na ikone domu a prace, pokud jsou v moje mapy vyplnene
		setTimeout(() => {
			if (!clear) {
				return;
			}
			clear.addEventListener("click", event => {
				event.stopPropagation();
				event.preventDefault();
				this._clearClick();
			});
		}, 300);
	}

	/**
	 * Ziskani obrazku itemu.
	 */
	_getImage(): HTMLDivElement {
		const image = node("div", { className: "image dad-handle" });
		let url = "";
		let srcSet = "";
		let favicon = "";
		let className = "";

		if (this._data.images && this._data.images["3x2"]) {
			url = Mapy.CDN.setSize(this._data.images["3x2"], Mapy.CDN.SIZE_80_65);
		} else if (this._data.favicon) {
			favicon = this._data.favicon;
			if (favicon) { // aby se neposilal pozadavek na null
				image.style.backgroundImage = `url(${favicon})`;
			}
		} else if (this._data.typeId) {
			url = getIcon(this._data.typeId);
			srcSet = `${url}?scale=2 1x, ${url}?scale=3 2x`;
			className = "poitype";
		}

		if (url) {
			const img = new Image();

			img.src = url;
			img.crossOrigin = "anonymous";

			if (srcSet !== "") {
				img.srcset = srcSet;
			}
			if (className !== "") {
				img.classList.add(className);
			}
			image.appendChild(img);
		} else if (favicon === "" || !favicon) {
			image.appendChild(icon("pin"));
		}

		return image;
	}

	/**
	 * Kliknuti na zruseni itemu.
	 */
	_clearClick() {
		this._data = null;
		Mapy.CML.state = {
			route: {
				zoom: true,
			},
		};
		this._parent.updateState();
		Mapy.getComponent("history").save();
	}

	/**
	 * Kliknuti na vytvoreni vyletu.
	 */
	_showTripClick() {
		this._data.planTrip = true;
		Mapy.CML.state = {
			route: {
				zoom: true,
				params: {
					type: ROUTE_CONST.TYPE_WALK,
				},
			},
		};
		this._parent.updateState();
		Mapy.getComponent("history").save();
	}
}
